import { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { 
    Mic2, 
    Music, 
    Award, 
    Instagram,
    Youtube,
    Star,
    Quote
} from 'lucide-react';
import { Link } from 'react-router-dom';

export const ArtistSection = () => {
    const containerRef = useRef(null);
    const isInView = useInView(containerRef, { once: true, margin: "-100px" });
    
    const { scrollYProgress } = useScroll({
        target: containerRef,
        offset: ['start end', 'end start'],
    });

    const imageY = useTransform(scrollYProgress, [0, 1], ['0%', '10%']);
    return (
        <section ref={containerRef} className="py-24 lg:py-32 relative overflow-hidden">
            {/* Background Effects */}
            <div className="absolute top-1/2 left-0 w-80 h-80 bg-accent/10 rounded-full blur-[120px] -translate-y-1/2 pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-[150px] pointer-events-none" />

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                {/* Section Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.7 }}
                    className="text-center mb-16 lg:mb-20"
                >
                    <div className="inline-flex items-center gap-2 mb-4">
                        <Mic2 className="w-5 h-5 text-primary" />
                        <span className="text-sm font-body font-medium text-primary uppercase tracking-wider">
                            The Face of Hawayein
                        </span>
                    </div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4">
                        Yash Singh Thakur
                    </h2>
                    <p className="text-muted-foreground text-base sm:text-lg font-body max-w-2xl mx-auto">
                        The charismatic voice and founder of <span className="text-primary font-semibold">Hawayein The Band</span> — 
                        India's Premier Sufi & Bollywood performer with 2000+ mesmerizing shows
                    </p>
                </motion.div>

                {/* Artist Card */}
                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={isInView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.8, delay: 0.2 }}
                    className="max-w-5xl mx-auto"
                >
                    <Card className="card-premium rounded-3xl overflow-hidden">
                        <CardContent className="p-0">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                                {/* Image Side */}
                                <motion.div 
                                    className="relative h-80 sm:h-96 lg:h-auto lg:min-h-[500px] overflow-hidden"
                                    style={{ y: imageY }}
                                >
                                    <img
                                        src="https://customer-assets.emergentagent.com/job_yash-thakur-band/artifacts/0tqq5lae_7.jpeg"
                                        alt="Yash Singh Thakur - Lead Vocalist"
                                        className="w-full h-full object-cover object-center"
                                    />
                                    {/* Gradient Overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-card" />
                                    
                                    {/* Award Badge */}
                                    <motion.div
                                        initial={{ scale: 0, opacity: 0 }}
                                        animate={isInView ? { scale: 1, opacity: 1 } : {}}
                                        transition={{ delay: 0.5, type: 'spring' }}
                                        className="absolute top-6 right-6"
                                    >
                                        <div className="glass px-4 py-2 rounded-full flex items-center gap-2">
                                            <Award className="w-4 h-4 text-amber" />
                                            <span className="text-sm font-body font-medium text-foreground">
                                                Award-Winning Artist
                                            </span>
                                        </div>
                                    </motion.div>
                                </motion.div>

                                {/* Content Side */}
                                <div className="p-8 lg:p-10 flex flex-col justify-center">
                                    <Badge 
                                        variant="outline" 
                                        className="w-fit mb-4 border-primary/40 bg-primary/10 text-primary"
                                    >
                                        🎤 Lead Vocalist & Founder - Hawayein The Band
                                    </Badge>
                                    
                                    <h3 className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-foreground mb-3">
                                        Yash Singh Thakur
                                    </h3>
                                    
                                    <p className="text-muted-foreground font-body leading-relaxed mb-6">
                                        With over 8 years of stage experience and <span className="text-primary font-semibold">2000+ live performances</span>, 
                                        Yash Singh Thakur is the heart and soul of Hawayein The Band. His mastery of 
                                        <span className="text-primary"> Sufi & Bollywood music</span> combined with electrifying stage presence 
                                        has made him one of Central India's most sought-after performers.
                                    </p>

                                    {/* Quote */}
                                    <div className="relative bg-muted/30 rounded-xl p-5 mb-6 border-l-4 border-primary">
                                        <Quote className="absolute top-4 right-4 w-8 h-8 text-muted-foreground/20" />
                                        <p className="text-foreground font-body italic text-sm sm:text-base">
                                            "Music is not just sound — it's the language that connects souls. 
                                            When I perform, I don't just sing songs, I share emotions. Every show is a journey."
                                        </p>
                                    </div>

                                    {/* Stats */}
                                    <div className="grid grid-cols-3 gap-4 mb-6">
                                        <div className="text-center p-3 rounded-xl bg-muted/30 border border-border/30">
                                            <Music className="w-5 h-5 text-primary mx-auto mb-1" />
                                            <p className="font-heading font-bold text-lg text-foreground">8+</p>
                                            <p className="text-xs text-muted-foreground font-body">Years Exp</p>
                                        </div>
                                        <div className="text-center p-3 rounded-xl bg-muted/30 border border-border/30">
                                            <Star className="w-5 h-5 text-amber mx-auto mb-1" />
                                            <p className="font-heading font-bold text-lg text-foreground">2000+</p>
                                            <p className="text-xs text-muted-foreground font-body">Shows</p>
                                        </div>
                                        <div className="text-center p-3 rounded-xl bg-muted/30 border border-border/30">
                                            <Award className="w-5 h-5 text-accent mx-auto mb-1" />
                                            <p className="font-heading font-bold text-lg text-foreground">15+</p>
                                            <p className="text-xs text-muted-foreground font-body">Awards</p>
                                        </div>
                                    </div>

                                    {/* Social & CTA */}
                                    <div className="flex flex-wrap items-center gap-3">
                                        <Link to="/book">
                                            <Button className="bg-gradient-to-r from-primary to-accent text-foreground font-heading font-semibold rounded-xl">
                                                Book Yash for Your Event
                                            </Button>
                                        </Link>
                                        <div className="flex gap-2">
                                            <motion.a
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.95 }}
                                                href="https://instagram.com/yashsinghthakur"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-10 h-10 rounded-lg bg-muted/50 border border-border/50 flex items-center justify-center text-muted-foreground hover:text-primary hover:border-primary/50 transition-colors"
                                            >
                                                <Instagram className="w-4 h-4" />
                                            </motion.a>
                                            <motion.a
                                                whileHover={{ scale: 1.1 }}
                                                whileTap={{ scale: 0.95 }}
                                                href="https://youtube.com/@yashsinghthakur"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="w-10 h-10 rounded-lg bg-muted/50 border border-border/50 flex items-center justify-center text-muted-foreground hover:text-destructive hover:border-destructive/50 transition-colors"
                                            >
                                                <Youtube className="w-4 h-4" />
                                            </motion.a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        </section>
    );
};
